import { store } from 'store/store';
import {
  setLocaleFile,
  setLocaleFileLoading,
  LocaleVariables,
} from 'store/slices/localesSlice';

const replaceVariables = (text: string, variables: LocaleVariables): string => {
  return Object.keys(variables).reduce((result, key) => {
    const regex = new RegExp(`{${key}}`, 'g');
    return result.replace(regex, String(variables[key]));
  }, text);
};

const pendingTranslationLoads: Partial<Record<string, Promise<void>>> = {};

const getTranslationCacheKey = (locale: string, file: string): string => {
  return `xovi:translations:v1:${locale}:${file}`;
};

const shouldPersistTranslationFile = (file: string): boolean => {
  return file === '_global';
};

const readTranslationsFromCache = (
  locale: string,
  file: string
): Record<string, string> | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  if (!shouldPersistTranslationFile(file)) {
    return null;
  }

  try {
    const rawValue = localStorage.getItem(getTranslationCacheKey(locale, file));

    if (!rawValue) {
      return null;
    }

    const translations = JSON.parse(rawValue) as Record<string, string>;

    if (!translations || typeof translations !== 'object') {
      return null;
    }

    return translations;
  } catch {
    return null;
  }
};

const writeTranslationsToCache = (
  locale: string,
  file: string,
  translations: Record<string, string>
) => {
  if (typeof window === 'undefined' || !shouldPersistTranslationFile(file)) {
    return;
  }

  try {
    localStorage.setItem(
      getTranslationCacheKey(locale, file),
      JSON.stringify(translations)
    );
  } catch {
    // Ignore localStorage write failures
  }
};

const loadTranslationsFromPublic = async (
  locale: string,
  file: string
): Promise<Record<string, string>> => {
  try {
    const response = await fetch(`/locales/${locale}/${file}.json`, {
      cache: 'no-store',
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache',
      },
    });
    if (!response.ok) {
      throw new Error(`Failed to load translations: ${response.status}`);
    }
    const translations = await response.json();

    if (Object.keys(translations).length) {
      writeTranslationsToCache(locale, file, translations);
    }

    return translations;
  } catch (error) {
    return {};
  }
};

const loadTranslations = (
  locale: string,
  file: string,
  preserveExisting: boolean = false
) => {
  const localeKey = `${locale}_${file}`;

  if (pendingTranslationLoads[localeKey]) {
    return pendingTranslationLoads[localeKey];
  }

  pendingTranslationLoads[localeKey] = loadTranslationsFromPublic(locale, file)
    .then(translations => {
      if (!Object.keys(translations).length && preserveExisting) {
        return;
      }

      store.dispatch(setLocaleFile({ locale, file, translations }));
    })
    .catch(() => {
      if (preserveExisting) {
        return;
      }

      store.dispatch(setLocaleFile({ locale, file, translations: {} }));
    })
    .finally(() => {
      delete pendingTranslationLoads[localeKey];
    });

  return pendingTranslationLoads[localeKey];
};

/**
 * Main translation function - works with public folder files
 * @param slug - Translation key
 * @param file - Translation file name (default: '_global')
 * @param fallback - Fallback text if translation not found
 * @param variables - Variables to replace in translation
 */
export const translate = (
  slug: string,
  file: string = '_global',
  fallback: string = '',
  variables: LocaleVariables | null = null
): string => {
  const state = store.getState();
  const { currentLocale, locales, loading } = state.locales;
  const localeKey = `${currentLocale}_${file}`;

  if (!locales[localeKey]) {
    const cachedTranslations = readTranslationsFromCache(currentLocale, file);

    if (cachedTranslations) {
      // Serve cached translations immediately and refresh them in background.
      void loadTranslations(currentLocale, file, true);

      const translation = cachedTranslations[slug];
      let result = translation || fallback || slug;

      if (variables && typeof variables === 'object' && translation) {
        result = replaceVariables(translation, variables);
      }

      return result;
    }

    if (loading[localeKey] || pendingTranslationLoads[localeKey]) {
      // Avoid returning empty string while loading
      return fallback || slug;
    }

    void loadTranslations(currentLocale, file);

    // Avoid returning empty string while loading
    return fallback || slug;
  }

  // If still loading, return fallback
  if (loading[localeKey]) {
    // Avoid returning empty string while loading
    return fallback || slug;
  }

  const translation = locales[localeKey]?.[slug];
  let result = translation || fallback || slug;

  if (variables && typeof variables === 'object' && translation) {
    result = replaceVariables(translation, variables);
  }

  return result;
};

export const preloadTranslations = async (
  locale: string,
  files: string[] = ['_global']
): Promise<void> => {
  const state = store.getState();
  const { locales, loading } = state.locales;

  const promises = files.map(async file => {
    const localeKey = `${locale}_${file}`;

    // Skip if file is already loaded or currently loading.
    if (locales[localeKey] || loading[localeKey]) {
      return;
    }

    const cachedTranslations = readTranslationsFromCache(locale, file);

    if (cachedTranslations) {
      // Use cached translations immediately and refresh them in background.
      store.dispatch(
        setLocaleFile({ locale, file, translations: cachedTranslations })
      );
      void loadTranslations(locale, file, true);
      return;
    }

    if (pendingTranslationLoads[localeKey]) {
      await pendingTranslationLoads[localeKey];
      return;
    }

    // Set loading state to prevent duplicate requests
    store.dispatch(setLocaleFileLoading({ locale, file, loading: true }));

    await loadTranslations(locale, file);
  });

  await Promise.all(promises);
};
